import { useLayoutEffect, useRef, useState } from 'react'
import { DEVICE_WIDTH, type PreviewDevice } from './previewUtils'

interface PreviewFrameProps {
  html: string
  device: PreviewDevice
  loading?: boolean
}

/**
 * Renders the server-rendered landing HTML inside a sandboxed iframe, scaled to
 * fit the available width at the chosen device size. `sandbox="allow-scripts"`
 * (without allow-same-origin) lets the landing's own JS run while keeping it fully
 * isolated from the admin panel.
 */
export const PreviewFrame = ({ html, device, loading }: PreviewFrameProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => setSize({ width: el.clientWidth, height: el.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const deviceWidth = DEVICE_WIDTH[device]
  const scale = size.width ? Math.min(1, size.width / deviceWidth) : 1
  // Make the iframe tall enough to fill the panel after scaling.
  const frameHeight = size.height ? size.height / scale : 900

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden rounded-lg border bg-muted/30"
    >
      {loading && (
        <div className="absolute right-3 top-3 z-10 rounded-md bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow">
          Actualizando…
        </div>
      )}
      <div className="flex h-full w-full justify-center">
        <iframe
          title="preview"
          sandbox="allow-scripts allow-popups"
          srcDoc={html}
          style={{
            width: deviceWidth,
            height: frameHeight,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            background: '#fff',
          }}
        />
      </div>
    </div>
  )
}
